import {
    Box,
    Button,
    ButtonBase,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    useTheme,
    type Theme,
} from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import DiscardChangesDialog from '~/components/SaveChanges/DiscardChangesDialog';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import StatusLine from '~/components/Status/StatusLine';
import type {
    PendingRegistrationChanges,
    PendingRoiChange,
    PendingStructureSetChanges,
    RoiChangeKind,
} from '~/domain/changes/PendingChangesStore';
import type { DicomColor } from '~/domain/dicom/dicomColor';
import type { SaveReport } from '~/domain/services/save/SaveOutcome';
import { usePendingChanges } from '~/hooks/save/usePendingChanges';
import { dicomColorToRgbString } from '~/utils/color';

const TITLE_ID = 'pending-changes-panel-title';

/** Stable display order of change-kind labels within one ROI line. */
const KIND_ORDER: readonly RoiChangeKind[] = [
    'added',
    'deleted',
    'renamed',
    'colorChanged',
    'lockChanged',
    'typeChanged',
    'approvalChanged',
    'contoursEdited',
    'edited',
];

const KIND_MESSAGE_ID: Record<RoiChangeKind, string> = {
    added: 'saveChanges.kind.added',
    deleted: 'saveChanges.kind.deleted',
    renamed: 'saveChanges.kind.renamed',
    colorChanged: 'saveChanges.kind.colorChanged',
    lockChanged: 'saveChanges.kind.lockChanged',
    typeChanged: 'saveChanges.kind.typeChanged',
    approvalChanged: 'saveChanges.kind.approvalChanged',
    contoursEdited: 'saveChanges.kind.contoursEdited',
    edited: 'saveChanges.kind.edited',
};

const STATUS_MESSAGE_ID: Record<PendingStructureSetChanges['status'], string> =
    {
        new: 'saveChanges.status.new',
        modified: 'saveChanges.status.modified',
        deleted: 'saveChanges.status.deleted',
    };

/** Dot color per pending status — same idiom as the patient list rows. */
const STATUS_TONE_COLORS: Record<
    PendingStructureSetChanges['status'],
    (theme: Theme) => string
> = {
    new: (theme) => theme.palette.success.main,
    modified: (theme) => theme.palette.warning.main,
    deleted: (theme) => theme.palette.error.main,
};

type Props = {
    open: boolean;
    onClose: () => void;
    saving: boolean;
    onSaveAll: () => void;
    /**
     * Discard every local modification and reload from the database. The
     * discard action is offered only when this is given.
     */
    onDiscardAndReload?: () => void;
    /** Last save attempt's report — failed entries get their reason shown. */
    report?: SaveReport | null;
    /** Other editors on this patient (advisory presence) — 0 when alone. */
    otherEditorCount?: number;
    /** Replaces the default title. */
    title?: string;
    /** Shown above the list, for example why the save is asked for. */
    message?: string;
    /** Replaces the default label of the save action. */
    saveLabel?: string;
};

/**
 * Review panel for the explicit save: one expandable row per pending
 * structure set (patient-list dataset-row styling: 13px rows, modality
 * tag chip, status dot) with per-ROI change lines underneath, a single
 * "Save all" action (SR-WOR-H-23-1 — no selective save), and, when the
 * caller offers it, a discard escape hatch that reloads the patient from the
 * database. Discarding is irreversible, so it takes a dedicated confirmation
 * dialog.
 */
export default function PendingChangesPanel({
    open,
    onClose,
    saving,
    onSaveAll,
    onDiscardAndReload,
    report = null,
    otherEditorCount = 0,
    title,
    message,
    saveLabel,
}: Props) {
    const intl = useIntl();
    const theme = useTheme();
    const { state } = usePendingChanges();
    const [confirmingDiscard, setConfirmingDiscard] = useState(false);
    const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
        new Set()
    );
    const entries = [...(state?.byStructureSet.values() ?? [])].sort(
        (a, b) =>
            a.displayName.localeCompare(b.displayName) ||
            a.structureSetId.localeCompare(b.structureSetId)
    );
    const registrationEntries = [
        ...(state?.byRegistration.values() ?? []),
    ].sort(
        (a, b) =>
            a.displayName.localeCompare(b.displayName) ||
            a.registrationId.localeCompare(b.registrationId)
    );
    const datasetEntries = [
        ...(state?.byImage.values() ?? []),
        ...(state?.byDose.values() ?? []),
    ].sort(
        (a, b) =>
            a.displayName.localeCompare(b.displayName) ||
            a.id.localeCompare(b.id)
    );
    const totalEntries =
        entries.length + registrationEntries.length + datasetEntries.length;
    // Why the last save left an entry pending — keyed by entity id. The
    // messages are the savers' static texts (no PHI); an entry that saved
    // or was never attempted has no note.
    const failureById = new Map<string, string>(
        (report?.outcomes ?? []).flatMap((o) =>
            o.kind === 'failed' ? [[o.id, o.message]] : []
        )
    );

    // A stale confirmation or expansion must not greet the next open.
    useEffect(() => {
        if (!open) {
            setConfirmingDiscard(false);
            setExpandedIds(new Set());
        }
    }, [open]);

    const toggleExpanded = (id: string): void => {
        setExpandedIds((current) => {
            const next = new Set(current);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const roiLine = (change: PendingRoiChange): string => {
        const kinds = KIND_ORDER.filter((kind) => change.kinds.has(kind)).map(
            (kind) =>
                intl.formatMessage({
                    // A point ROI has no contours — its geometry edit is
                    // a move.
                    id:
                        kind === 'contoursEdited' && change.isPointRoi
                            ? 'saveChanges.kind.pointMoved'
                            : KIND_MESSAGE_ID[kind],
                })
        );
        return kinds.join(', ');
    };

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            aria-labelledby={TITLE_ID}
            fullWidth
            maxWidth="sm"
            PaperProps={{ elevation: 0 }}
        >
            <DialogTitle id={TITLE_ID}>
                {title ?? intl.formatMessage({ id: 'saveChanges.panelTitle' })}
            </DialogTitle>
            <DialogContent>
                {message && (
                    <Typography
                        sx={{ fontSize: 13, mb: 1.5 }}
                        data-testid="pending-changes-message"
                    >
                        <SensitiveInfoTypography>
                            {message}
                        </SensitiveInfoTypography>
                    </Typography>
                )}
                {otherEditorCount > 0 && (
                    <Typography
                        sx={{ fontSize: 12, color: 'warning.main', mb: 1 }}
                    >
                        {intl.formatMessage(
                            { id: 'saveChanges.otherEditors' },
                            { count: otherEditorCount }
                        )}
                    </Typography>
                )}
                {totalEntries === 0 ? (
                    <Typography color="text.secondary">
                        {intl.formatMessage({ id: 'saveChanges.nothing' })}
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            py: 0.5,
                        }}
                    >
                        {entries.map((entry) => {
                            const isExpanded = expandedIds.has(
                                entry.structureSetId
                            );
                            return (
                                <Box key={entry.structureSetId}>
                                    <ButtonBase
                                        onClick={() =>
                                            toggleExpanded(entry.structureSetId)
                                        }
                                        aria-expanded={isExpanded}
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'flex-start',
                                            alignItems: 'center',
                                            gap: 1,
                                            height: 32,
                                            px: 1.5,
                                            fontSize: 13,
                                            textAlign: 'left',
                                            transition: 'background 100ms',
                                            '&:hover, &:focus-visible': {
                                                bgcolor: 'action.hoverNeutral',
                                            },
                                        }}
                                    >
                                        <Chip variant="tag" label="RTSTRUCT" />
                                        <Typography
                                            component="span"
                                            noWrap
                                            sx={{
                                                fontSize: 13,
                                                color: 'text.primary',
                                            }}
                                        >
                                            <SensitiveInfoTypography>
                                                {entry.displayName}
                                            </SensitiveInfoTypography>
                                        </Typography>
                                        <Typography
                                            component="span"
                                            noWrap
                                            sx={{
                                                fontSize: 11,
                                                color: 'action.disabled',
                                                flexShrink: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            {entry.roiChanges.size > 0
                                                ? intl.formatMessage(
                                                      {
                                                          id: 'saveChanges.structureCountChanged',
                                                      },
                                                      {
                                                          changed:
                                                              entry.roiChanges
                                                                  .size,
                                                          count: entry.roiCount,
                                                      }
                                                  )
                                                : intl.formatMessage(
                                                      {
                                                          id: 'saveChanges.structureCount',
                                                      },
                                                      {
                                                          count: entry.roiCount,
                                                      }
                                                  )}
                                        </Typography>
                                        <Box sx={{ flex: 1 }} />
                                        {entry.lastModifiedAt !== null && (
                                            <Typography
                                                variant="monoMeta"
                                                sx={{ flexShrink: 0 }}
                                                title={intl.formatDate(
                                                    entry.lastModifiedAt,
                                                    {
                                                        dateStyle: 'medium',
                                                        timeStyle: 'medium',
                                                    }
                                                )}
                                            >
                                                {intl.formatTime(
                                                    entry.lastModifiedAt
                                                )}
                                            </Typography>
                                        )}
                                        {entry.approvalDirty && (
                                            <Typography
                                                component="span"
                                                noWrap
                                                sx={{
                                                    fontSize: 11,
                                                    color: 'info.main',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {intl.formatMessage({
                                                    id: 'saveChanges.approvalChange',
                                                })}
                                            </Typography>
                                        )}
                                        <Box
                                            component="span"
                                            sx={{ flexShrink: 0 }}
                                        >
                                            <StatusLine
                                                label={intl.formatMessage({
                                                    id: STATUS_MESSAGE_ID[
                                                        entry.status
                                                    ],
                                                })}
                                                color={STATUS_TONE_COLORS[
                                                    entry.status
                                                ](theme)}
                                                size={11}
                                            />
                                        </Box>
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'inline-flex',
                                                flexShrink: 0,
                                                color: 'text.secondary',
                                            }}
                                        >
                                            <ChevronDown
                                                size={14}
                                                aria-hidden
                                                style={{
                                                    transform: isExpanded
                                                        ? 'rotate(180deg)'
                                                        : undefined,
                                                    transition:
                                                        'transform 100ms',
                                                }}
                                            />
                                        </Box>
                                    </ButtonBase>
                                    <FailureNote
                                        message={failureById.get(
                                            entry.structureSetId
                                        )}
                                    />
                                    {isExpanded && (
                                        <Box sx={{ pb: 0.5 }}>
                                            {entry.status === 'deleted' ? (
                                                <DetailNote
                                                    text={intl.formatMessage({
                                                        id: 'saveChanges.deletedDetail',
                                                    })}
                                                />
                                            ) : entry.roiChanges.size === 0 ? (
                                                <DetailNote
                                                    text={intl.formatMessage({
                                                        id: 'saveChanges.metadataOnly',
                                                    })}
                                                />
                                            ) : (
                                                [...entry.roiChanges.values()]
                                                    .sort(
                                                        (a, b) =>
                                                            a.roiNumber -
                                                            b.roiNumber
                                                    )
                                                    .map((change) => (
                                                        <RoiChangeRow
                                                            key={change.roiId}
                                                            change={change}
                                                            line={roiLine(
                                                                change
                                                            )}
                                                        />
                                                    ))
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                        {registrationEntries.map((entry) => {
                            const isExpanded = expandedIds.has(
                                entry.registrationId
                            );
                            return (
                                <Box key={entry.registrationId}>
                                    <ButtonBase
                                        onClick={() =>
                                            toggleExpanded(entry.registrationId)
                                        }
                                        aria-expanded={isExpanded}
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'flex-start',
                                            alignItems: 'center',
                                            gap: 1,
                                            height: 32,
                                            px: 1.5,
                                            fontSize: 13,
                                            textAlign: 'left',
                                            transition: 'background 100ms',
                                            '&:hover, &:focus-visible': {
                                                bgcolor: 'action.hoverNeutral',
                                            },
                                        }}
                                    >
                                        <Chip variant="tag" label="REG" />
                                        <Typography
                                            component="span"
                                            noWrap
                                            sx={{
                                                fontSize: 13,
                                                color: 'text.primary',
                                            }}
                                        >
                                            <SensitiveInfoTypography>
                                                {entry.displayName}
                                            </SensitiveInfoTypography>
                                        </Typography>
                                        <Box sx={{ flex: 1 }} />
                                        {entry.lastModifiedAt !== null && (
                                            <Typography
                                                variant="monoMeta"
                                                sx={{ flexShrink: 0 }}
                                                title={intl.formatDate(
                                                    entry.lastModifiedAt,
                                                    {
                                                        dateStyle: 'medium',
                                                        timeStyle: 'medium',
                                                    }
                                                )}
                                            >
                                                {intl.formatTime(
                                                    entry.lastModifiedAt
                                                )}
                                            </Typography>
                                        )}
                                        {entry.approvalDirty && (
                                            <Typography
                                                component="span"
                                                noWrap
                                                sx={{
                                                    fontSize: 11,
                                                    color: 'info.main',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {intl.formatMessage({
                                                    id: 'saveChanges.approvalChange',
                                                })}
                                            </Typography>
                                        )}
                                        <Box
                                            component="span"
                                            sx={{ flexShrink: 0 }}
                                        >
                                            <StatusLine
                                                label={intl.formatMessage({
                                                    id: STATUS_MESSAGE_ID[
                                                        entry.status
                                                    ],
                                                })}
                                                color={STATUS_TONE_COLORS[
                                                    entry.status
                                                ](theme)}
                                                size={11}
                                            />
                                        </Box>
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'inline-flex',
                                                flexShrink: 0,
                                                color: 'text.secondary',
                                            }}
                                        >
                                            <ChevronDown
                                                size={14}
                                                aria-hidden
                                                style={{
                                                    transform: isExpanded
                                                        ? 'rotate(180deg)'
                                                        : undefined,
                                                    transition:
                                                        'transform 100ms',
                                                }}
                                            />
                                        </Box>
                                    </ButtonBase>
                                    <FailureNote
                                        message={failureById.get(
                                            entry.registrationId
                                        )}
                                    />
                                    {isExpanded && (
                                        <Box sx={{ pb: 0.5 }}>
                                            {entry.status === 'deleted' ? (
                                                <DetailNote
                                                    text={intl.formatMessage({
                                                        id: 'saveChanges.deletedDetail',
                                                    })}
                                                />
                                            ) : (
                                                registrationChangeLines(
                                                    entry,
                                                    intl.formatMessage
                                                ).map((line) => (
                                                    <DetailNote
                                                        key={line}
                                                        text={line}
                                                    />
                                                ))
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                        {datasetEntries.map((entry) => (
                            <Box key={entry.id}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        height: 32,
                                        px: 1.5,
                                        fontSize: 13,
                                    }}
                                >
                                    <Chip
                                        variant="tag"
                                        label={entry.modality || 'DATA'}
                                    />
                                    <Typography
                                        component="span"
                                        noWrap
                                        sx={{
                                            fontSize: 13,
                                            color: 'text.primary',
                                        }}
                                    >
                                        <SensitiveInfoTypography>
                                            {entry.displayName}
                                        </SensitiveInfoTypography>
                                    </Typography>
                                    <Box sx={{ flex: 1 }} />
                                    {entry.lastModifiedAt !== null && (
                                        <Typography
                                            variant="monoMeta"
                                            sx={{ flexShrink: 0 }}
                                            title={intl.formatDate(
                                                entry.lastModifiedAt,
                                                {
                                                    dateStyle: 'medium',
                                                    timeStyle: 'medium',
                                                }
                                            )}
                                        >
                                            {intl.formatTime(
                                                entry.lastModifiedAt
                                            )}
                                        </Typography>
                                    )}
                                    {entry.labelDirty && (
                                        <Typography
                                            component="span"
                                            noWrap
                                            sx={{
                                                fontSize: 11,
                                                color: 'info.main',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {intl.formatMessage({
                                                id: 'saveChanges.kind.renamed',
                                            })}
                                        </Typography>
                                    )}
                                    <Box
                                        component="span"
                                        sx={{ flexShrink: 0 }}
                                    >
                                        <StatusLine
                                            label={intl.formatMessage({
                                                id: STATUS_MESSAGE_ID[
                                                    entry.status
                                                ],
                                            })}
                                            color={STATUS_TONE_COLORS[
                                                entry.status
                                            ](theme)}
                                            size={11}
                                        />
                                    </Box>
                                </Box>
                                <FailureNote
                                    message={failureById.get(entry.id)}
                                />
                            </Box>
                        ))}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {onDiscardAndReload && (
                    <Button
                        onClick={() => setConfirmingDiscard(true)}
                        color="error"
                        disabled={saving || totalEntries === 0}
                        sx={{ mr: 'auto' }}
                        data-testid="discard-changes-button"
                    >
                        {intl.formatMessage({ id: 'saveChanges.discardAll' })}
                    </Button>
                )}
                <Button onClick={onClose} color="inherit" disabled={saving}>
                    {intl.formatMessage({ id: 'saveChanges.cancel' })}
                </Button>
                <Button
                    onClick={onSaveAll}
                    variant="contained"
                    disabled={saving || totalEntries === 0}
                    data-testid="save-all-button"
                >
                    {saving
                        ? intl.formatMessage({ id: 'saveChanges.saving' })
                        : (saveLabel ??
                          intl.formatMessage({ id: 'saveChanges.save' }))}
                </Button>
            </DialogActions>
            {onDiscardAndReload && (
                <DiscardChangesDialog
                    open={confirmingDiscard}
                    onCancel={() => setConfirmingDiscard(false)}
                    onConfirm={() => {
                        setConfirmingDiscard(false);
                        onDiscardAndReload();
                    }}
                />
            )}
        </Dialog>
    );
}

/** What changed on a modified registration, as translated display lines. */
function registrationChangeLines(
    entry: PendingRegistrationChanges,
    formatMessage: (descriptor: { id: string }) => string
): string[] {
    const lines: string[] = [];
    if (entry.status === 'new') {
        lines.push(formatMessage({ id: 'saveChanges.reg.created' }));
    }
    if (entry.matrixDirty) {
        lines.push(formatMessage({ id: 'saveChanges.reg.matrixChanged' }));
    }
    if (entry.labelDirty) {
        lines.push(formatMessage({ id: 'saveChanges.reg.renamed' }));
    }
    if (entry.descriptionDirty) {
        lines.push(formatMessage({ id: 'saveChanges.reg.descriptionChanged' }));
    }
    if (entry.approvalDirty) {
        lines.push(formatMessage({ id: 'saveChanges.reg.approvalChanged' }));
    }
    return lines;
}

/**
 * Why the last save left this row pending — shown without expanding, so
 * the reason for a failed save is visible the moment the panel opens.
 */
function FailureNote({ message }: { message: string | undefined }) {
    const intl = useIntl();
    if (!message) {
        return null;
    }
    return (
        <Typography
            role="alert"
            sx={{
                fontSize: 11,
                color: 'error.main',
                pl: 3,
                pr: 1.5,
                pb: 0.5,
            }}
        >
            {intl.formatMessage(
                { id: 'saveChanges.rowSaveFailed' },
                { message }
            )}
        </Typography>
    );
}

/** Secondary note inside an expanded row (deleted / metadata-only). */
function DetailNote({ text }: { text: string }) {
    return (
        <Typography
            sx={{
                fontSize: 11,
                color: 'text.secondary',
                pl: 3,
                pr: 1.5,
                py: 0.5,
            }}
        >
            {text}
        </Typography>
    );
}

/** One changed-ROI line: colour swatch(es), name, change kinds. */
function RoiChangeRow({
    change,
    line,
}: {
    change: PendingRoiChange;
    line: string;
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                height: 26,
                pl: 3,
                pr: 1.5,
                fontSize: 13,
            }}
        >
            {change.previousColor ? (
                // Colour change: old → new, so the edit is verifiable
                // against what the viewer showed before.
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        flexShrink: 0,
                    }}
                >
                    <ColorSwatch color={change.previousColor} />
                    <Box
                        component="span"
                        sx={{ fontSize: 11, color: 'action.disabled' }}
                        aria-hidden
                    >
                        →
                    </Box>
                    <ColorSwatch color={change.color} />
                </Box>
            ) : (
                <ColorSwatch color={change.color} />
            )}
            <Typography
                component="span"
                noWrap
                sx={{ fontSize: 13, color: 'text.primary' }}
            >
                {change.displayName}
            </Typography>
            <Typography
                component="span"
                noWrap
                sx={{
                    fontSize: 11,
                    color: 'action.disabled',
                    flexShrink: 1,
                    minWidth: 0,
                }}
            >
                {line}
            </Typography>
        </Box>
    );
}

/** The ROI's contour colour as shown in the viewer. */
function ColorSwatch({ color }: { color: Readonly<DicomColor> }) {
    return (
        <Box
            component="span"
            aria-hidden
            data-testid="roi-color-swatch"
            sx={{
                width: 10,
                height: 10,
                flexShrink: 0,
                borderRadius: 0.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: dicomColorToRgbString(color),
            }}
        />
    );
}
